import React, { useCallback, useEffect, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Layout } from "../components/Layout";
import { AppLayout } from "../components/AppLayout/AppLayout";
import { fetchApi } from "../common/api";
import { supabase } from "../common/supabase";
import { Account } from "../pages/Account/Account";
import { APIKeys } from "../pages/APIKeys/APIKeys";
import { ChatPage } from "../pages/Chat/ChatPage";
import { MemoryDetail } from "../pages/MemoryDetail/MemoryDetail";
import { Memories } from "../pages/Memories/Memories";
import { SignIn } from "../pages/SignIn/SignIn";
import { UpdateDetailPage } from "../pages/UpdateDetailPage";
import { UpdatesPage } from "../pages/UpdatesPage";
import { setUserAction } from "../state/user/actions";
import { User } from "../state/user/types";
import { State } from "../state/state";
import { store } from "../state/store";
import appStyles from "../styles/AppClasses.module.scss";
import authStyles from "../pages/SignIn/styles.stylesheet.module.scss";
import {
  getAccountPath,
  getMemoriesPath,
  getSignInPath,
  getApiKeysPath,
  getDeveloperPath,
} from "./routes";

const LoadingScreen = () => (
  <main className={authStyles.authStatusPage}>
    <div className={appStyles.spinner} aria-label="Loading" />
  </main>
);

const AuthRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector((state: State) => state.user.user);
  if (user) {
    return <Navigate to={getMemoriesPath()} replace />;
  }
  return children;
};

const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector((state: State) => state.user.user);
  const location = useLocation();
  if (!user) {
    return <Navigate to={getSignInPath()} replace state={{ from: location }} />;
  }
  return children;
};

const RouterContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const initSession = useCallback(async () => {
    setIsLoading(true);
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.user) {
      const userProfile = await fetchApi<User>(
        "/users/get_by_auth_id",
        "POST",
        {
          auth_id: session.user.id,
        },
      );
      await setUserAction(userProfile)(dispatch);
    }

    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    initSession();
  }, [initSession]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        dispatch({ type: "CLEAR_REDUX_STATE" });
        navigate(getSignInPath(), { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [dispatch, navigate]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/updates" element={<UpdatesPage />} />
        <Route path="/updates/:idOrSlug" element={<UpdateDetailPage />} />
      </Route>

      <Route path="/" element={<Navigate to={getMemoriesPath()} replace />} />

      <Route
        path={getSignInPath()}
        element={
          <AuthRoute>
            <SignIn />
          </AuthRoute>
        }
      />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={getMemoriesPath()} replace />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="chat/:conversationId" element={<ChatPage />} />
        <Route path="memories" element={<Memories />} />
        <Route path="memories/:memoryId" element={<MemoryDetail />} />
        <Route
          path={getAccountPath().replace("/app/", "")}
          element={<Account />}
        />
        <Route
          path={getDeveloperPath().replace("/app/", "")}
          element={<APIKeys />}
        />
        <Route
          path={getApiKeysPath().replace("/app/", "")}
          element={<Navigate to={getDeveloperPath()} replace />}
        />
      </Route>

      <Route path="*" element={<Navigate to={getMemoriesPath()} replace />} />
    </Routes>
  );
};

export const Router = () => (
  <Provider store={store}>
    <RouterContent />
  </Provider>
);
